
import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSearchFormLogic } from '@/hooks/useSearchFormLogic';
import { useIsMobile } from '@/hooks/use-mobile';
import SearchFormDesktop from './SearchFormDesktop';
import SearchFormMobile from './SearchFormMobile';
import SearchFormBooking from './SearchFormBooking';
import SimplifiedSearchSummary from './SimplifiedSearchSummary';

interface SearchFormProps {
  className?: string;
  showTitle?: boolean;
  variant?: 'default' | 'booking';
  onDateChange?: (checkIn: Date | undefined, checkOut: Date | undefined) => void;
  selectedCheckIn?: Date | undefined;
  selectedCheckOut?: Date | undefined;
  maxGuests?: number;
  unavailableDates?: Date[];
  showSimplified?: boolean;
}

const SearchForm = ({
  className = '',
  showTitle = true,
  variant = 'default',
  onDateChange,
  selectedCheckIn,
  selectedCheckOut,
  maxGuests = 6,
  unavailableDates = [],
  showSimplified = false
}: SearchFormProps) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const searchFormLogic = useSearchFormLogic({
    variant,
    onDateChange,
    selectedCheckIn,
    selectedCheckOut,
    maxGuests
  });

  const handleSearch = () => {
    searchFormLogic.handleSearch();
    // Collapse back to simplified view on mobile after search
    if (isMobile && showSimplified) {
      setIsExpanded(false);
    }
  };

  const handleSimplifiedClick = () => {
    setIsExpanded(true);
  };

  // Show simplified version only on mobile for search results variant
  const shouldShowSimplified = isMobile && showSimplified && !isExpanded;

  if (variant === 'booking') {
    return (
      <SearchFormBooking
        checkIn={searchFormLogic.checkIn}
        checkOut={searchFormLogic.checkOut}
        guests={searchFormLogic.guests}
        onCheckInChange={searchFormLogic.handleCheckInChange}
        onCheckOutChange={searchFormLogic.handleCheckOutChange}
        onGuestsChange={searchFormLogic.setGuests}
        maxGuests={maxGuests}
        unavailableDates={unavailableDates}
      />
    );
  }

  if (shouldShowSimplified) {
    return (
      <SimplifiedSearchSummary
        checkIn={searchFormLogic.checkIn}
        checkOut={searchFormLogic.checkOut}
        guests={searchFormLogic.guests}
        onClick={handleSimplifiedClick}
      />
    );
  }

  return (
    <div className="">
      {showTitle && <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('home.hero.search_form_title')}</h3>}
      
      <SearchFormDesktop
        checkIn={searchFormLogic.checkIn}
        checkOut={searchFormLogic.checkOut}
        guests={searchFormLogic.guests}
        onCheckInChange={searchFormLogic.handleCheckInChange}
        onCheckOutChange={searchFormLogic.handleCheckOutChange}
        onGuestsChange={searchFormLogic.setGuests}
        onSearch={handleSearch}
        maxGuests={maxGuests}
        unavailableDates={unavailableDates}
      />

      <SearchFormMobile
        checkIn={searchFormLogic.checkIn}
        checkOut={searchFormLogic.checkOut}
        guests={searchFormLogic.guests}
        onCheckInChange={searchFormLogic.handleCheckInChange}
        onCheckOutChange={searchFormLogic.handleCheckOutChange}
        onGuestsChange={searchFormLogic.setGuests}
        onSearch={handleSearch}
        maxGuests={maxGuests}
        unavailableDates={unavailableDates}
      />
    </div>
  );
};

export default SearchForm;
